import { useTick } from '@pixi/react';
import type { Sprite, Ticker } from 'pixi.js';
import { useCallback, useRef } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';
import { glowTexture } from '../glowTexture';

export interface GlowLight {
  x: number;
  y: number;
  /** Glow size in the parent's coordinates. */
  width: number;
  height: number;
  color: number;
  /** Peak opacity, 0-1. */
  intensity: number;
  /** `pulse` breathes slowly; `flicker` adds faint irregular dips, like a lamp. */
  motion: 'pulse' | 'flicker' | 'still';
}

/** Additive glows over painted light sources. Animates opacity only; geometry never changes. */
export function GlowLights({ lights }: { lights: readonly GlowLight[] }) {
  const sprites = useRef<(Sprite | null)[]>([]);
  const reducedMotion = useReducedMotion();

  const animate = useCallback(
    (ticker: Ticker) => {
      if (reducedMotion) return;
      const t = ticker.lastTime / 1000;
      lights.forEach((light, i) => {
        const sprite = sprites.current[i];
        if (!sprite) return;
        let level = 1;
        if (light.motion === 'pulse') level = 0.7 + 0.3 * Math.sin(t * 1.3 + i * 0.9);
        if (light.motion === 'flicker') {
          level = 0.92 + 0.08 * Math.sin(t * 7 + i * 3.1) * Math.sin(t * 2.3 + i);
        }
        sprite.alpha = light.intensity * level;
      });
    },
    [lights, reducedMotion],
  );
  useTick(animate);

  return (
    <>
      {lights.map((light, i) => (
        <pixiSprite
          key={i}
          ref={(sprite: Sprite | null) => {
            sprites.current[i] = sprite;
          }}
          texture={glowTexture()}
          anchor={0.5}
          x={light.x}
          y={light.y}
          width={light.width}
          height={light.height}
          tint={light.color}
          alpha={light.intensity}
          blendMode="add"
        />
      ))}
    </>
  );
}
